import { ExternalLinkIcon, DeleteIcon, AddIcon } from "@chakra-ui/icons";
import {
  VStack,
  Text,
  Box,
  Table,
  Tr,
  Th,
  Tbody,
  Spinner,
  Td,
  MenuButton,
  IconButton,
  MenuList,
  Menu,
  HStack,
  Link,
  Button,
  useDisclosure,
} from "@chakra-ui/react";

import {
  AccountAccessRoleName,
  AccountPermission,
} from "@syla/shared/types/models/AccountAccessBase";
import { ActivityTypeName } from "@syla/shared/types/models/AccountBase";
import { AccountAccessDetails } from "@syla/shared/types/responses/GetAccountsResponse";
import { SubscriptionWithPlansAndAccess } from "@syla/shared/types/responses/GetSubscriptionsResponse";
import { getCurrentSubscription } from "@syla/shared/types/subscriptions/getCurrentSubscription";
import { getSubscriptionState } from "@syla/shared/types/subscriptions/getSubscriptionState";
import { Lifecycle } from "@syla/shared/types/subscriptions/subscriptionState";
import { sortBy } from "lodash";
import React, { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { HiDotsVertical } from "react-icons/hi";
import { Link as RouterLink } from "react-router-dom";
import {
  PageContainer,
  PageHeadingContainer,
  StdVStack,
} from "../../components/atoms/Containers";
import { PageHeading } from "../../components/atoms/Headings";
import {
  StickyThead,
  HEADER_Z_INDEX,
} from "../../components/atoms/StickyThead";
import { tableFrameProps } from "../../components/atoms/tableFrameProps";
import { DestructiveMenuItem } from "../../components/DestructiveMenuItem";
import {
  PageHeadingTagContainer,
  PageHeadingTag,
} from "../../components/molecules/PageHeadingTag";
import { AccountNameEditable } from "../../components/organisms/accounts/AccountNameEditable";
import { DeleteAccountModal } from "../../components/organisms/accounts/DeleteAccountModal";
import { NicknameEditable } from "../../components/organisms/accounts/NicknameEditable";
import { RemoveAccountModal } from "../../components/organisms/accounts/RemoveAccountModal";
import { CreateAccountModal } from "../../components/organisms/navbar/CreateAccountModal";
import { getUserDetails } from "../../helper/getUserDetails";
import { hasAccountPermission } from "../../helper/permissions";
import { ReactComponent as CornerUpRight } from "../../images/icons/cornerUpRight.svg";
import {
  buildAccountSettingsLink,
  buildDataSourcesLink,
} from "../../routers/routePaths";
import { useQueryGetAccounts } from "../../store/useQueryGetAccounts";
import { AccountSettingsPanel } from "../AccountSettings";
import { RenewalDateCell } from "./RenewalDataCell";

const HELP_ARTICLE =
  "https://help.syla.com.au/en/articles/9092794-managing-multiple-accounts";

export const Accounts = () => {
  const createAccountDisclosure = useDisclosure();

  const { data: accountsResponse } = useQueryGetAccounts();

  return (
    <PageContainer>
      <PageHeadingContainer>
        <StdVStack spacing={6}>
          <StdVStack>
            <PageHeadingTagContainer>
              <PageHeading>Accounts</PageHeading>
              {accountsResponse != null && (
                <PageHeadingTag>
                  {Object.values(
                    accountsResponse.accounts,
                  ).length.toLocaleString()}
                </PageHeadingTag>
              )}
            </PageHeadingTagContainer>
            <Text color="gray.800">
              Manage all the accounts you have access to
            </Text>
          </StdVStack>
          <Link
            href={HELP_ARTICLE}
            fontWeight="bold"
            color="primary.500"
            isExternal
          >
            Guide to managing accounts <ExternalLinkIcon />
          </Link>
        </StdVStack>

        <Button
          variant="solid"
          colorScheme="primary"
          leftIcon={<AddIcon />}
          onClick={createAccountDisclosure.onOpen}
          flexShrink={0}
        >
          Add account
          {createAccountDisclosure.isOpen && (
            <CreateAccountModal disclosure={createAccountDisclosure} />
          )}
        </Button>
      </PageHeadingContainer>

      <VStack alignItems={"flex-start"} minHeight="50vh" overflowY="scroll">
        <Box width="100%" overflowX="auto" {...tableFrameProps}>
          <AccountsTable />
        </Box>
      </VStack>
    </PageContainer>
  );
};

function AccountsTable() {
  const { data: accountsResponse } = useQueryGetAccounts();

  if (!accountsResponse) return <Spinner />;

  const accounts = Object.values(accountsResponse.accounts);

  return (
    <Table>
      <StickyThead>
        <Tr>
          <Th>Open</Th>
          <Th>Account Name</Th>
          <Th>Nickname</Th>
          <Th>Account</Th>
          <Th>Activity</Th>
          <Th>Role</Th>
          <Th>Members</Th>
          <Th>Subscription</Th>
          <Th>Renewal Date</Th>
          <Th>Billing Owner</Th>
          <Th></Th>
        </Tr>
      </StickyThead>
      <Tbody>
        {sortBy(accounts, (account) => account._id).map((account) => (
          <AccountsRow key={account._id} account={account} />
        ))}
      </Tbody>
    </Table>
  );
}

function AccountsRow({ account }: { account: AccountAccessDetails }) {
  const deleteAccountDisclosure = useDisclosure();
  const removeAccountDisclosure = useDisclosure();

  const subscription = getCurrentSubscription(account.subscriptions);

  const formProps = useForm<{
    nickname: string;
    fullName: string;
  }>({
    defaultValues: account,
  });

  const { reset } = formProps;

  // reset form on data updates
  useEffect(() => {
    reset(account);
  }, [account, reset]);

  return (
    <FormProvider {...formProps}>
      <Tr key={account._id}>
        {/* Open */}
        <Td align="center">
          <IconButton
            as={RouterLink}
            to={buildDataSourcesLink({ accountId: account._id })}
            size="sm"
            borderWidth={1}
            icon={<CornerUpRight />}
            aria-label="Open"
          />
        </Td>

        {/* Account name */}
        <Td minWidth="12rem">
          <AccountNameEditable
            account={account}
            zIndex={HEADER_Z_INDEX - 1}
          ></AccountNameEditable>
        </Td>

        {/* Nickname */}
        <Td minWidth="10rem">
          <NicknameEditable account={account} zIndex={HEADER_Z_INDEX - 1} />
        </Td>
        {/* Account Type */}
        <Td>{account.accountType}</Td>

        {/* Activity Type */}
        <Td>{ActivityTypeName[account.accountSubType]}</Td>

        {/* Role */}
        <Td>{AccountAccessRoleName[account.role]}</Td>

        {/* Members */}
        <Td>
          <HStack
            as={RouterLink}
            to={buildAccountSettingsLink({
              accountId: account._id,
              panel: AccountSettingsPanel.Members,
            })}
          >
            <Text>
              {account.access.length === 1 ?
                `Just you`
              : `You and ${account.access.length - 1} others`}
            </Text>
            <ExternalLinkIcon />
          </HStack>
        </Td>

        {/* Subscription */}
        <Td>
          <HStack
            as={RouterLink}
            to={buildAccountSettingsLink({
              accountId: account._id,
              panel: AccountSettingsPanel.Subscription,
            })}
          >
            <Text>{subscription ? subscription.plan.name : "None"}</Text>
            <ExternalLinkIcon />
          </HStack>
        </Td>

        <Td>
          <RenewalDateCell subscription={subscription} />
        </Td>

        {/* Billing Owner */}
        <Td>{getBillingOwnerCell(subscription)}</Td>

        {/* Overflow Menu */}
        <Td>
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<HiDotsVertical />}
              backgroundColor="transparent"
            />
            <MenuList p="0">
              {!account.onlyOwner && (
                <DestructiveMenuItem
                  onClick={removeAccountDisclosure.onOpen}
                  icon={<DeleteIcon />}
                >
                  Remove account
                </DestructiveMenuItem>
              )}
              {hasAccountPermission(
                account,
                AccountPermission.DeleteAccount,
              ) && (
                <DestructiveMenuItem
                  onClick={deleteAccountDisclosure.onOpen}
                  icon={<DeleteIcon />}
                >
                  Delete account
                </DestructiveMenuItem>
              )}
            </MenuList>
          </Menu>
        </Td>
      </Tr>

      {deleteAccountDisclosure.isOpen && (
        <DeleteAccountModal
          account={account}
          disclosure={deleteAccountDisclosure}
        />
      )}

      {removeAccountDisclosure.isOpen && (
        <RemoveAccountModal
          account={account}
          disclosure={removeAccountDisclosure}
        />
      )}
    </FormProvider>
  );
}

function getBillingOwnerCell(subscription?: SubscriptionWithPlansAndAccess) {
  if (
    !subscription ||
    getSubscriptionState(subscription).lifecycle === Lifecycle.Terminated
  ) {
    return <Text>N/A</Text>;
  }

  if (subscription.billingAccountAccess) {
    return <Text>You</Text>;
  }

  if (subscription.billingOwner) {
    return <Text>{getUserDetails(subscription.billingOwner)}</Text>;
  }

  // manual plan has no billingOwner
  return <Text>None</Text>;
}
